export interface DistriConfig {
  clientId: string;
  agentId: string;
  theme?: 'dark' | 'light';
  container?: HTMLElement | string;
  baseUrl?: string;
  threadId?: string;
  tools?: any[];
  enableHistory?: boolean;
  initialMessages?: any[];
  models?: any[];
  selectedModelId?: string;
  voiceEnabled?: boolean;
  useSpeechRecognition?: boolean;
  ttsConfig?: any;
  initialInput?: string;
  allowBrowserPreview?: boolean;
  maxWidth?: string;
  emptyState?: any;
}

export type EmbedMessage =
  | { type: 'distri:ready' }
  | { type: 'distri:message'; data: unknown }
  | { type: 'distri:error'; error: string }
  | { type: 'distri:resize'; height: number }
  | { type: 'distri:refresh_token' }
  | { type: 'distri:token'; token: string; expires_at?: string }
  | { type: 'distri:call_tool'; callId: string; toolName: string; input: any }
  | { type: 'distri:send'; content: string }
  | { type: 'distri:stop' }
  | { type: 'distri:trigger_tool'; toolName: string; input: any }
  | { type: 'distri:token_update'; token: string }
  | { type: 'distri:config'; tools?: any[] };

export function sendToParent(msg: EmbedMessage) {
  if (window.parent !== window) {
    window.parent.postMessage(msg, '*')
  }
}

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

export const getThemeClasses = (theme: 'light' | 'dark' | 'auto') => {
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return '';
};

export function safeJsonParse(val: string | null) {
  if (!val) return undefined;
  try {
    return JSON.parse(val);
  } catch (e) {
    console.error('[Embed] Failed to parse JSON parameter:', e);
    return undefined;
  }
}

export function getUrlParams() {
  const params = new URLSearchParams(window.location.search)
  return {
    clientId: params.get('clientId') || '',
    agentId: params.get('agentId') || '',
    theme: (params.get('theme') as 'dark' | 'light') || 'dark',
    threadId: params.get('threadId') || '',
    enableHistory: params.get('enableHistory') === 'true',
    initialInput: params.get('initialInput') || undefined,
    voiceEnabled: params.get('voiceEnabled') === 'true',
    useSpeechRecognition: params.get('useSpeechRecognition') === 'true',
    allowBrowserPreview: params.get('allowBrowserPreview') !== 'false',
    maxWidth: params.get('maxWidth') || undefined,
    selectedModelId: params.get('selectedModelId') || undefined,
    ttsConfig: safeJsonParse(params.get('ttsConfig')),
    models: safeJsonParse(params.get('models')),
    initialMessages: safeJsonParse(params.get('initialMessages')),
    emptyState: safeJsonParse(params.get('emptyState')),
    mode: params.get('mode') || 'token', // 'token' or 'full'
    baseUrl: params.get('baseUrl') || undefined,
    workspaceId: params.get('workspaceId') || undefined,
  }
}

export async function fetchEmbedToken(
  baseUrl: string,
  clientId: string,
  turnstileToken: string,
  workspaceId?: string,
): Promise<{ token: string; expires_at: string; scopes: string[] }> {
  // Remove all trailing slashes
  const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (workspaceId) {
    headers['X-Workspace-Id'] = workspaceId;
  }
  const response = await fetch(`${cleanBaseUrl}/embed/token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      client_id: clientId,
      turnstile_token: turnstileToken,
    }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Failed to get embed token' }))
    throw new Error(data.error || 'Failed to get embed token')
  }
  return response.json()
}
